import { useEffect, useState } from 'react';
import { X, Users, Phone, StickyNote, Repeat, UserCheck, UserX, Trash2, Pencil, Loader2, ChevronRight, CalendarClock, Receipt } from 'lucide-react';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { SECTION_COLOR, SECTION_BG, fmtKc, Nadpis } from '../objednavky/ui';
import { FazeOdznak, predDny } from '../zakaznici/ui';
import { STAVY, api, cil, denDlouze, oknoPopis } from './rezervaceUi';

/**
 * Hosté → Rezervace → detail rezervace (druhý sloupec, na mobilu celá
 * obrazovka se šipkou zpět).
 *
 * Stavové akce jsou tady na povrchu (Posadit, Nedorazili, Zrušit) — u pultu
 * se dělají jedním klepnutím. Úprava údajů otevře formulář.
 *
 * Pod rezervací je **host**: rezervace si zákazníka dohledá podle telefonu
 * (`RezervaceService::dohledejZakaznika`), takže obsluha vidí, jestli jde
 * o stálého hosta, kolik u nás nechává a co si dává — dřív to bylo
 * o modul vedle. Kdo nemá záložku Zákazníci, souhrn nevidí.
 */
export default function RezervaceDetail({ rezervace: r, isDark, isMobile, mutedColor, muzeZakaznici, onZpet, onUpravit, onZmena, onZakaznik, onObjednavka, rezervaDole = null }) {
    const [pracuje, setPracuje] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [vyber, setVyber] = useState(null);   // null | 'posadit' | 'zrusit'
    const [stolyKPosazeni, setStolyKPosazeni] = useState(null);
    const [host, setHost] = useState(null);

    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';
    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const sekce = { padding: isMobile ? '14px 16px' : '16px 22px', borderTop: `1px solid ${cara}` };
    const stav = STAVY[r.stav] || STAVY.potvrzena;
    const aktivni = r.stav === 'potvrzena' || r.stav === 'nedorazila';

    useEffect(() => { setVyber(null); setChyba(null); setStolyKPosazeni(null); }, [r.id]);

    useEffect(() => {
        if (!r.zakaznik_id || !muzeZakaznici) { setHost(null); return undefined; }
        let platne = true;
        setHost('nacita');
        fetch(`/api/zakaznici/${r.zakaznik_id}`, { headers: { Accept: 'application/json' } })
            .then(res => (res.ok ? res.json() : null))
            .then(d => { if (platne) setHost(d); })
            .catch(() => { if (platne) setHost(null); });
        return () => { platne = false; };
    }, [r.zakaznik_id, muzeZakaznici]);

    const spustit = async (fn) => {
        if (pracuje) return;
        setPracuje(true);
        setChyba(null);
        try { await fn(); }
        catch (e) { setChyba(e.message || 'Nepodařilo se to uložit.'); }
        finally { setPracuje(false); }
    };

    const posadit = (stulId = null) => spustit(async () => {
        await api(`/api/pos/rezervace/${r.id}/posadit`, 'POST', { stul_id: stulId });
        setVyber(null);
        onZmena();
    });

    // Na který stůl si sedli? U jednoho stolu se neptáme; u celé sekce
    // se stoly sekce dotáhnou z dostupnosti (rezervace je sama nenese).
    const zacitPosazeni = () => spustit(async () => {
        let stoly = r.stoly || [];
        if (r.cela_sekce) {
            const q = new URLSearchParams({ zacatek: r.zacatek, konec: r.konec, cely_den: r.cely_den ? '1' : '0', krome_id: String(r.id) });
            const d = await api(`/api/pos/rezervace/dostupnost?${q}`);
            stoly = (d.stoly || []).filter(s => s.zona_id === r.zona?.id);
        }
        if (stoly.length > 1) {
            setStolyKPosazeni(stoly);
            setVyber('posadit');
            return;
        }
        await api(`/api/pos/rezervace/${r.id}/posadit`, 'POST', { stul_id: stoly[0]?.id ?? null });
        onZmena();
    });

    const zmenitStav = (akce, serie = false) => spustit(async () => {
        await api(`/api/pos/rezervace/${r.id}/${akce}`, 'POST', { serie });
        setVyber(null);
        onZmena();
    });

    const tlacitko = (primarni = false, barva = null) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        minHeight: isMobile ? 44 : 36, padding: '0 14px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        border: primarni ? 'none' : `1px solid ${hranice}`,
        background: primarni ? (barva || SECTION_COLOR) : 'transparent',
        color: primarni ? '#fff' : (barva || 'var(--text-primary)'),
        opacity: pracuje ? 0.6 : 1, WebkitTapHighlightColor: 'transparent',
    });

    const radekInfo = (Ikona, obsah) => (
        <div className="flex items-start" style={{ gap: 10, padding: '6px 0', fontSize: 13.5, color: 'var(--text-primary)' }}>
            <Ikona size={15} style={{ color: mutedColor, flexShrink: 0, marginTop: 2 }} />
            <div className="min-w-0" style={{ overflowWrap: 'anywhere' }}>{obsah}</div>
        </div>
    );

    return (
        <div className="flex flex-col flex-1 min-h-0" style={{ overflowY: 'auto' }}>
            {/* Hlavička */}
            <div style={{ padding: isMobile ? '8px 16px 14px' : '18px 22px 16px' }}>
                {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na rezervace" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div className="flex items-start" style={{ gap: 12 }}>
                    <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 10, background: SECTION_BG }}>
                        <CalendarClock size={19} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="truncate" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{r.jmeno}</h2>
                        <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            {denDlouze(r.datum)} · {oknoPopis(r)}
                        </p>
                    </div>
                    <span className="rounded-full shrink-0" style={{ padding: '3px 10px', fontSize: 11.5, fontWeight: 700, background: `${stav.color}26`, color: stav.color }}>{stav.label}</span>
                    {!isMobile && (
                        <button type="button" onClick={onZpet} aria-label="Zavřít detail" title="Zpět na přehled"
                            className="flex items-center justify-center rounded-lg shrink-0"
                            style={{ width: 32, height: 32, border: 'none', background: 'transparent', color: mutedColor, cursor: 'pointer' }}>
                            <X size={17} />
                        </button>
                    )}
                </div>

                {/* Akce — stav jedním klepnutím, úprava ve formuláři */}
                <div className="flex flex-wrap" style={{ gap: 8, marginTop: 14 }}>
                    {aktivni && vyber === null && (
                        <>
                            <button type="button" disabled={pracuje} onClick={zacitPosazeni} style={tlacitko(true)}>
                                {pracuje ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={15} />} Posadit
                            </button>
                            {r.stav !== 'nedorazila' && (
                                <button type="button" disabled={pracuje} onClick={() => zmenitStav('nedorazila')} style={tlacitko()}>
                                    <UserX size={15} /> Nedorazili
                                </button>
                            )}
                        </>
                    )}
                    {r.stav !== 'zrusena' && vyber === null && (
                        <button type="button" disabled={pracuje} onClick={onUpravit} style={tlacitko()}>
                            <Pencil size={14} /> Upravit
                        </button>
                    )}
                    {aktivni && vyber === null && (
                        // U opakované rezervace se musíme zeptat — zrušit omylem
                        // celou sérii kvůli jednomu termínu je horší než klik navíc.
                        <button type="button" disabled={pracuje} style={tlacitko(false, '#EF4444')}
                            onClick={() => (r.serie_id ? setVyber('zrusit') : zmenitStav('zrusit', false))}>
                            <Trash2 size={14} /> Zrušit
                        </button>
                    )}
                </div>

                {vyber === 'posadit' && (
                    <div style={{ marginTop: 12 }}>
                        <Nadpis muted={mutedColor}>Na který stůl si sedli?</Nadpis>
                        <div className="grid grid-cols-3 sm:grid-cols-4" style={{ gap: 6, marginTop: 6 }}>
                            {(stolyKPosazeni || []).map(s => (
                                <button key={s.id} type="button" disabled={pracuje} onClick={() => posadit(s.id)} style={tlacitko()}>
                                    {s.nazev || `Stůl ${s.cislo}`}
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={() => setVyber(null)} style={{ ...tlacitko(), border: 'none', marginTop: 6, color: mutedColor }}>Zpět</button>
                    </div>
                )}
                {vyber === 'zrusit' && (
                    <div style={{ marginTop: 12 }}>
                        <Nadpis muted={mutedColor}>Zrušit jen tenhle termín, nebo celou sérii?</Nadpis>
                        <div className="flex flex-wrap" style={{ gap: 8, marginTop: 6 }}>
                            <button type="button" disabled={pracuje} onClick={() => zmenitStav('zrusit', false)} style={tlacitko()}>Jen tenhle termín</button>
                            <button type="button" disabled={pracuje} onClick={() => zmenitStav('zrusit', true)} style={tlacitko(true, '#EF4444')}>Celou sérii</button>
                            <button type="button" onClick={() => setVyber(null)} style={{ ...tlacitko(), border: 'none', color: mutedColor }}>Nechat být</button>
                        </div>
                    </div>
                )}
                {chyba && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#EF4444' }}>{chyba}</p>}
            </div>

            {/* Rezervace */}
            <div style={sekce}>
                <Nadpis muted={mutedColor}>Rezervace</Nadpis>
                {radekInfo(Users, <><b>{r.pocet_hostu}</b> {r.pocet_hostu === 1 ? 'host' : r.pocet_hostu < 5 ? 'hosté' : 'hostů'} · {cil(r)}</>)}
                {r.telefon && radekInfo(Phone, <a href={`tel:${r.telefon.replace(/\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>{r.telefon}</a>)}
                {r.poznamka && radekInfo(StickyNote, r.poznamka)}
                {r.serie_id && radekInfo(Repeat, <span style={{ color: 'var(--text-secondary)' }}>Termín opakované rezervace</span>)}
                {r.objednavka_id && onObjednavka && radekInfo(Receipt, (
                    <button type="button" onClick={() => onObjednavka(r.objednavka_id)} className="inline-flex items-center"
                        style={{ gap: 4, fontSize: 13.5, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                        Účet ze stolu <ChevronRight size={14} />
                    </button>
                ))}
                <p style={{ margin: '6px 0 0', fontSize: 11.5, color: mutedColor }}>
                    Založeno {r.zdroj === 'os' ? 'v systému' : 'v pokladně'}{r.zalozil ? ` · ${r.zalozil}` : ''}
                </p>
            </div>

            {/* Host */}
            {muzeZakaznici && (
                <div style={sekce}>
                    <Nadpis muted={mutedColor}>Host</Nadpis>
                    {!r.zakaznik_id && (
                        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: mutedColor, lineHeight: 1.45 }}>
                            {r.telefon ? 'Telefon nesedí na žádného zákazníka — host u nás zatím nic neobjednal.' : 'Bez telefonu se host nedá spárovat se zákazníkem.'}
                        </p>
                    )}
                    {host === 'nacita' && <Loader2 size={18} className="animate-spin" style={{ color: mutedColor, marginTop: 8 }} />}
                    {host && host !== 'nacita' && (
                        <HostSouhrn data={host} isDark={isDark} mutedColor={mutedColor} cara={cara}
                            onOtevrit={onZakaznik ? () => onZakaznik(r.zakaznik_id) : null} />
                    )}
                </div>
            )}

            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole, flexShrink: 0 }} />}
        </div>
    );
}

function HostSouhrn({ data, isDark, mutedColor, cara, onOtevrit }) {
    const s = data.souhrn;
    const rs = data.rezervace_souhrn || { pocet: (data.rezervace || []).length, nedorazila: 0 };
    const radek = (popis, hodnota, barva) => (
        <div className="flex justify-between" style={{ gap: 10, padding: '6px 0', borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{popis}</span>
            <b style={{ color: barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{hodnota}</b>
        </div>
    );
    return (
        <div style={{ marginTop: 6 }}>
            <div className="flex items-center" style={{ gap: 8, marginBottom: 6 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{data.zakaznik.jmeno || 'bez jména'}</span>
                <FazeOdznak faze={s.faze} isDark={isDark} />
            </div>
            {radek('Objednávek za 12 měsíců', s.pocet_12m)}
            {radek('Útrata za 12 měsíců', fmtKc(s.utrata_12m))}
            {radek('Naposledy', s.posledni ? predDny(s.dni_od_posledni) : '—')}
            {radek('Rezervací', rs.pocet)}
            {rs.nedorazila > 0 && radek('Nedorazili', `${rs.nedorazila}×`, '#F59E0B')}
            {data.oblibene?.length > 0 && radek('Nejčastěji', data.oblibene[0].nazev)}
            {data.zakaznik.poznamka_interni && (
                <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: 8, padding: '8px 10px', lineHeight: 1.45 }}>
                    {data.zakaznik.poznamka_interni}
                </p>
            )}
            {onOtevrit && (
                <button type="button" onClick={onOtevrit} className="inline-flex items-center"
                    style={{ marginTop: 10, gap: 4, fontSize: 13, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer', minHeight: 32 }}>
                    Otevřít kartu hosta <ChevronRight size={14} />
                </button>
            )}
            {!onOtevrit && <p style={{ margin: '8px 0 0', fontSize: 11.5, color: mutedColor }}>{fmtKc(s.utrata)} celkem</p>}
        </div>
    );
}
